@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.main.view.map

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.Divider
import androidx.compose.material.OutlinedButton
import androidx.compose.material.icons.Icons
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp

import com.nonopn.icons.Certificate
import com.nonopn.skydome.common.app.home.HotPreviewApp
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.navigation.RouteCertFoudr
import com.nonopn.skydome.common.app.navigation.RouteEstimateNew
import com.nonopn.skydome.common.app.navigation.RouterCertFoudr
import com.nonopn.skydome.common.app.navigation.RouterEstimateNew
import com.nonopn.skydome.common.app.navigation.RouterEstimateNewAdmin
import com.nonopn.skydome.common.app.pages.main.LocalMapScreenModel
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.main.model.api.PulseType
import com.nonopn.skydome.common.app.pages.main.model.api.ThunderDetectionActivity
import com.nonopn.skydome.common.app.utils.fmt
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.cert_foudr_screen_title
import com.nonopn.skywatcher.common.app.res.distance_to_kernel
import com.nonopn.skywatcher.common.app.res.number_sensors
import com.nonopn.skywatcher.common.app.res.probability_incidence
import com.nonopn.skywatcher.design.local.LocalFontSizes
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.localized
import com.nonopn.skywatcher.design.widgets.DefaultCard
import com.nonopn.skywatcher.maps.LatLng
import com.nonopn.skywatcher.maps.MapUtils
import com.nonopn.skywatcher.maps.testMahalanobisKMP

import de.drick.compose.hotpreview.HotPreview
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.compose.widgets.VectorIcon

import korlibs.time.DateFormat
import korlibs.time.DateTimeTz

import org.jetbrains.compose.resources.stringResource

import kotlin.math.abs
import kotlin.math.pow
import kotlin.math.round
import kotlin.math.roundToLong

@Suppress("LongMethod")
@Composable
fun ShowThunderActivityInfo(
    modifier: Modifier,
    activity: ThunderDetectionActivity,
    selectedPOIOnMap: LatLng? = null,
    selectedProject: Project? = null,
) {
    val appModel = LocalApp.current
    val mapModel = LocalMapScreenModel.current
    val mapState by mapModel.states.collectAsState()
    val appState by appModel.states.collectAsState()
    val isAdmin = appState.apiProvider?.apiAdmin != null
    var showNoCoverageDialog by remember { mutableStateOf(false) }

    val amp = if (abs(activity.pulsePeakAmp) >= 1000) {
        "${activity.pulsePeakAmp / 1000}kA"
    } else {
        "${activity.pulsePeakAmp}A"
    }

    val date = DateTimeTz.fromUnix(activity.recTimestampSeconds * 1000)
    val probability = selectedPOIOnMap?.let {
        testMahalanobisKMP(
            position1 = LatLng(activity.latitude, activity.longitude),
            position2 = LatLng(it.latitude, it.longitude),
            a = activity.ellipseSemiMinorMeterOrDefault.toDouble(),
            b = activity.ellipseSemiMajorMeterOrDefault.toDouble(),
            thetaDeg = activity.ellipseAngleDegree.toDouble()
        )
    }?.takeIf { !it.preciseProbability.isNaN() }

    if (showNoCoverageDialog) {
        NoProbeCoverageDialog(
            onAddProbe = {
                showNoCoverageDialog = false
                appModel.show(
                    // An administrator stays in the administrator screens (and menu)
                    if (appModel.states.value.isAdminMode) {
                        RouterEstimateNewAdmin.navigateTo()
                    } else {
                        RouterEstimateNew.navigateTo()
                    }
                )
            }.takeIf { isAdmin },
            onDismiss = { showNoCoverageDialog = false }
        )
    }

    DefaultCard(
        modifier = modifier,
        backgroundColor = AppColor.White,
        columnModifier = Modifier.padding(12.dp)
    ) {
        Column(
            Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            TextNormal(
                text = amp,
                fontSize = LocalFontSizes.current.map.callOutBig,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center,
                color = AppColor.StrikeText
            )
            TextNormal(
                text = date.format(FORMAT_DATE),
                fontSize = LocalFontSizes.current.map.callOutNormal,
                textAlign = TextAlign.Center,
                color = AppColor.StrikeText
            )

            selectedPOIOnMap?.let {
                Spacer(Modifier.height(4.dp))

                Divider(
                    Modifier.fillMaxWidth().padding(horizontal = 8.dp),
                    color = AppColor.GrayExtraLight
                )

                Spacer(Modifier.height(4.dp))

                val distance = MapUtils.distance(
                    it.latitude,
                    it.longitude,
                    activity.latitude,
                    activity.longitude
                )

                val text = if (distance >= 1) {
                    "${distance.distanceRounded(2)}km"
                } else {
                    "${round(distance * 1000)}m"
                }

                TextNormal(
                    text = stringResource(Res.string.distance_to_kernel, text),
                    fontSize = LocalFontSizes.current.map.callOutNormal,
                    textAlign = TextAlign.Center,
                    color = AppColor.StrikeText
                )

                TextNormal(
                    text = stringResource(Res.string.number_sensors, "${activity.pulseNumSensors}"),
                    fontSize = LocalFontSizes.current.map.callOutNormal,
                    textAlign = TextAlign.Center,
                    color = AppColor.StrikeText
                )

                probability?.let {
                    Spacer(Modifier.height(4.dp))

                    TextNormal(
                        text = Res.string.probability_incidence.localized(),
                        fontSize = LocalFontSizes.current.map.callOutNormal,
                        textAlign = TextAlign.Center,
                        color = AppColor.StrikeText
                    )

                    TextNormal(
                        "${(probability.preciseProbability * 100).distanceRounded(1)} %",
                        fontSize = LocalFontSizes.current.map.callOutBig,
                        fontWeight = FontWeight.Bold,
                        color = AppColor.StrikeText
                    )

                    Spacer(Modifier.height(4.dp))

                    OutlinedButton(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp),
                        onClick = {
                            val poiLat = selectedPOIOnMap.latitude
                            val poiLon = selectedPOIOnMap.longitude
                            if (isWithinProbeCoverage(
                                    poiLat,
                                    poiLon,
                                    mapState.probes
                                )
                            ) {
                                val pctValue = probability.preciseProbability * 100
                                val siteName = selectedProject?.name
                                    ?: "${poiLat.fmt(4)}\u00B0, " +
                                    "${poiLon.fmt(4)}\u00B0"
                                appModel.show(
                                    RouterCertFoudr.navigateTo(
                                        RouteCertFoudr(
                                            projectUuid = selectedProject?.uuid.orEmpty(),
                                            projectName = siteName,
                                            projectLat = poiLat,
                                            projectLon = poiLon,
                                            impactLat = activity.latitude,
                                            impactLon = activity.longitude,
                                            impactTimestamp = activity.recTimestampSeconds,
                                            impactAmplitude = activity.pulsePeakAmp,
                                            impactPercent = pctValue,
                                            impactExternalId = activity.externalId,
                                        )
                                    )
                                )
                            } else {
                                showNoCoverageDialog = true
                            }
                        }
                    ) {
                        VectorIcon(
                            modifier = Modifier.size(18.dp),
                            imageVector = Icons.Rounded.Certificate,
                            color = AppColor.StrikePrimary
                        )
                        Spacer(Modifier.width(8.dp))
                        TextNormal(
                            text = Res.string.cert_foudr_screen_title.localized(),
                            color = AppColor.StrikeText,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }
    }
}

private val FORMAT_DATE = DateFormat("yyyy-MM-dd HH:mm:ss")

fun Double.distanceRounded(digit: Int): Double {
    val digits = 10.0.pow(digit).roundToLong()
    return round(this * digits) / digits
}

@Composable
@HotPreview(widthDp = 260, heightDp = 250, darkMode = true)
@HotPreview(widthDp = 260, heightDp = 250, darkMode = false)
private fun ShowThunderActivityInfoPreview() {
    HotPreviewApp(Modifier.fillMaxSize(), isDarkTheme = isSystemInDarkTheme()) {
        ShowThunderActivityInfo(
            Modifier.fillMaxSize(),
            ThunderDetectionActivity(
                externalId = " ",
                pulseType = PulseType.CG,
                pulsePeakAmp = 115000,
                pulseNumSensors = 12,
                ellipseSemiMajorMeter = 7053,
                ellipseSemiMinorMeter = 1537,
                ellipseAngleDegree = 102
            ),
            selectedPOIOnMap = LatLng(
                latitude = 11.606349,
                longitude = 104.856865,
            )
        )
    }
}
